import React, { useState, useEffect, useCallback } from 'react';
import { apiUrl } from '../utils/api';
import './SiteOverviewModal.css';

const SiteOverviewModal = ({ isOpen, onClose, url }) => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchOverview = useCallback(async () => {
    if (!url) return;

    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(apiUrl('/api/compliance/public-overview'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ url }),
      });

      const data = await response.json();
      
      if (data.ok && data.data) {
        setOverview(data.data);
      } else {
        setError(data.message || 'Failed to load site overview');
      }
    } catch (err) {
      console.error('Error fetching site overview:', err);
      setError('Failed to load site overview. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    if (isOpen && url) {
      fetchOverview();
    }
  }, [isOpen, url, fetchOverview]);

  if (!isOpen) return null;

  return (
    <div className="site-overview-modal-overlay" onClick={onClose}>
      <div className="site-overview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="site-overview-modal-header">
          <h2>Site Overview</h2>
          <button className="site-overview-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="site-overview-modal-content">
          {loading && (
            <div className="site-overview-loading">
              <div className="site-overview-spinner"></div>
              <p>Analyzing site...</p>
            </div>
          )}

          {error && (
            <div className="site-overview-error">
              <p>{error}</p>
              <button onClick={fetchOverview} className="site-overview-retry-btn">
                Try Again
              </button>
            </div>
          )}

          {overview && !loading && !error && (
            <>
              {overview.title && (
                <div className="site-overview-section">
                  <h3>{overview.title}</h3>
                  {overview.description && (
                    <p className="site-overview-description">{overview.description}</p>
                  )}
                </div>
              )}

              <div className="site-overview-cards">
                <div className="site-overview-card">
                  <h4>Total Pages</h4>
                  <div className="site-overview-metric-value">{overview.totalPages || 0}</div>
                  <span className="site-overview-metric-caption">Indexed content</span>
                </div>

                <div className="site-overview-card">
                  <h4>Avg Content Length</h4>
                  <div className="site-overview-metric-value">{overview.avgWordCount?.toLocaleString() || '—'}</div>
                  <span className="site-overview-metric-caption">Words per page</span>
                </div>

                <div className="site-overview-card">
                  <h4>Homepage Images</h4>
                  <div className="site-overview-metric-value">{overview.homepageImages || 0}</div>
                  <span className="site-overview-metric-caption">Images detected</span>
                </div>

                <div className="site-overview-card">
                  <h4>SEO Setup</h4>
                  <div className="site-overview-metric-value">
                    {overview.hasSitemap && overview.hasRobotsTxt ? '✓ Complete' : 'Partial'}
                  </div>
                  <span className="site-overview-metric-caption">
                    {overview.hasSitemap ? 'Sitemap' : 'No sitemap'} • {overview.hasRobotsTxt ? 'robots.txt' : 'No robots.txt'}
                  </span>
                </div>

                <div className="site-overview-card">
                  <h4>AdShop Authority</h4>
                  <div className="site-overview-metric-value">
                    {overview.seoMetrics?.adShopAuthority || '—'}
                  </div>
                  <span className="site-overview-metric-caption">Connect account for score</span>
                </div>

                <div className="site-overview-card">
                  <h4>Backlinks</h4>
                  <div className="site-overview-metric-value">
                    {overview.seoMetrics?.referringDomains?.toLocaleString() || '—'}
                  </div>
                  <span className="site-overview-metric-caption">Connect account for data</span>
                </div>

                <div className="site-overview-card">
                  <h4>Keywords</h4>
                  <div className="site-overview-metric-value">
                    {overview.seoMetrics?.organicKeywords?.toLocaleString() || '—'}
                  </div>
                  <span className="site-overview-metric-caption">Connect account for data</span>
                </div>
              </div>

              {overview.note && (
                <div className="site-overview-note">
                  <p>{overview.note}</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SiteOverviewModal;
